// One icon per resource type, the same everywhere (REDISENO_UI.md 1.8): the
// sidebar, Files, search, lineage, the resource header and the recents list
// all ask this map, so a pipeline is never drawn as `Workflow` in one place
// and `GitBranch` or `Network` in another.
//
// The icon takes its type's category color (a Blueprint extended color, from
// resource.css); everything else in the interface stays monochrome. The
// colors live in CSS so this file has no hex (hexRatchet) and dark mode lifts
// them in one rule.
import React from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AppWindow, Bell, BookOpen, Bot, Box, BrainCircuit, CalendarClock, ChartColumn, Database, DraftingCompass,
  FileText, FlaskConical, Folder, FolderKanban, GitBranch, GitPullRequest, LayoutDashboard, LayoutGrid,
  Link2, Package, Plug, ScanSearch, Sparkles, Table2, Target, User, Users, Waypoints, Workflow, Wrench, Zap,
  BarChart2, TrendingUp, Compass, MousePointerClick,
} from 'lucide-react';
import './components/resource.css';

/** Every kind of thing a user can open, name, star or find in recents. */
export type ResourceKind =
  | 'connector' | 'dataset' | 'pipeline' | 'lineage' | 'utility'
  | 'object_type' | 'object' | 'link' | 'action_type'
  | 'logic_function' | 'agent' | 'app' | 'dashboard' | 'notebook' | 'eval_suite'
  | 'process' | 'analysis' | 'insight' | 'prediction' | 'scenario' | 'document' | 'solution_design'
  | 'value_case' | 'discovery' | 'task_mining' | 'mirror'
  | 'project' | 'folder' | 'branch' | 'change_request' | 'solution'
  | 'schedule' | 'alert' | 'user' | 'group' | 'page';

/** Category hues (resource.css --res-hue-*). `neutral` is the muted text color. */
export type ResourceHue =
  | 'cerulean' | 'turquoise' | 'blue' | 'gold' | 'lime' | 'violet' | 'rose' | 'sepia' | 'vermilion' | 'neutral';

export interface ResourceIconDef {
  icon: LucideIcon;
  hue: ResourceHue;
  /** i18n key of the type's name ("Pipeline", "Tipo de objeto"). */
  labelKey: string;
  label: string;
}

export const RESOURCE_ICONS: Record<ResourceKind, ResourceIconDef> = {
  connector:       { icon: Plug,            hue: 'cerulean',  labelKey: 'resource.kind.connector',      label: 'Fuente de datos' },
  dataset:         { icon: Table2,          hue: 'cerulean',  labelKey: 'resource.kind.dataset',        label: 'Dataset' },
  pipeline:        { icon: Workflow,        hue: 'turquoise', labelKey: 'resource.kind.pipeline',       label: 'Pipeline' },
  lineage:         { icon: Waypoints,       hue: 'turquoise', labelKey: 'resource.kind.lineage',        label: 'Linaje' },
  utility:         { icon: Wrench,          hue: 'neutral',   labelKey: 'resource.kind.utility',        label: 'Utilidad' },
  object_type:     { icon: Box,             hue: 'blue',      labelKey: 'resource.kind.objectType',     label: 'Tipo de objeto' },
  // A record is drawn with its type's icon; callers pass the type's own color.
  object:          { icon: Box,             hue: 'blue',      labelKey: 'resource.kind.object',         label: 'Registro' },
  link:            { icon: Link2,           hue: 'blue',      labelKey: 'resource.kind.link',           label: 'Enlace' },
  action_type:     { icon: Zap,             hue: 'gold',      labelKey: 'resource.kind.actionType',     label: 'Tipo de acción' },
  logic_function:  { icon: BrainCircuit,    hue: 'lime',      labelKey: 'resource.kind.logicFunction',  label: 'Función' },
  // Violet is for AI only (1.3): agents and AI findings.
  agent:           { icon: Bot,             hue: 'violet',    labelKey: 'resource.kind.agent',          label: 'Agente' },
  app:             { icon: AppWindow,       hue: 'rose',      labelKey: 'resource.kind.app',            label: 'Aplicación' },
  dashboard:       { icon: LayoutDashboard, hue: 'rose',      labelKey: 'resource.kind.dashboard',      label: 'Dashboard' },
  notebook:        { icon: BookOpen,        hue: 'sepia',     labelKey: 'resource.kind.notebook',       label: 'Cuaderno' },
  eval_suite:      { icon: FlaskConical,    hue: 'vermilion', labelKey: 'resource.kind.evalSuite',      label: 'Suite de evaluación' },
  process:         { icon: ScanSearch,      hue: 'vermilion', labelKey: 'resource.kind.process',        label: 'Proceso' },
  analysis:        { icon: ChartColumn,     hue: 'sepia',     labelKey: 'resource.kind.analysis',       label: 'Análisis' },
  insight:         { icon: Sparkles,        hue: 'violet',    labelKey: 'resource.kind.insight',        label: 'Hallazgo' },
  prediction:      { icon: Target,          hue: 'gold',      labelKey: 'resource.kind.prediction',     label: 'Predicción' },
  scenario:        { icon: BarChart2,       hue: 'gold',      labelKey: 'resource.kind.scenario',       label: 'Escenario' },
  value_case:      { icon: TrendingUp,      hue: 'gold',      labelKey: 'resource.kind.valueCase',      label: 'Caso de valor' },
  discovery:       { icon: Compass,         hue: 'vermilion', labelKey: 'resource.kind.discovery',      label: 'Proyecto de descubrimiento' },
  task_mining:     { icon: MousePointerClick, hue: 'sepia',   labelKey: 'resource.kind.taskMining',     label: 'Proyecto de task mining' },
  mirror:          { icon: ScanSearch,      hue: 'vermilion', labelKey: 'resource.kind.mirror',         label: 'Proceso del Espejo' },
  document:        { icon: FileText,        hue: 'rose',      labelKey: 'resource.kind.document',       label: 'Documento' },
  solution_design: { icon: DraftingCompass, hue: 'rose',      labelKey: 'resource.kind.solutionDesign', label: 'Diseño de solución' },
  project:         { icon: FolderKanban,    hue: 'neutral',   labelKey: 'resource.kind.project',        label: 'Proyecto' },
  folder:          { icon: Folder,          hue: 'neutral',   labelKey: 'resource.kind.folder',         label: 'Carpeta' },
  branch:          { icon: GitBranch,       hue: 'neutral',   labelKey: 'resource.kind.branch',         label: 'Rama' },
  change_request:  { icon: GitPullRequest,  hue: 'neutral',   labelKey: 'resource.kind.changeRequest',  label: 'Solicitud de cambio' },
  solution:        { icon: Package,         hue: 'neutral',   labelKey: 'resource.kind.solution',       label: 'Solución' },
  schedule:        { icon: CalendarClock,   hue: 'neutral',   labelKey: 'resource.kind.schedule',       label: 'Programación' },
  alert:           { icon: Bell,            hue: 'neutral',   labelKey: 'resource.kind.alert',          label: 'Alerta' },
  user:            { icon: User,            hue: 'neutral',   labelKey: 'resource.kind.user',           label: 'Usuario' },
  group:           { icon: Users,           hue: 'neutral',   labelKey: 'resource.kind.group',          label: 'Grupo' },
  page:            { icon: LayoutGrid,      hue: 'neutral',   labelKey: 'resource.kind.page',           label: 'Página' },
};

/** Database stays the icon of the Data page (sidebar); a dataset is a table. */
export const DATA_PAGE_ICON: LucideIcon = Database;

export function resourceIconDef(kind: ResourceKind | string): ResourceIconDef {
  return RESOURCE_ICONS[kind as ResourceKind] ?? RESOURCE_ICONS.page;
}

/** CSS color of a hue, for callers that paint something else in it (a node border). */
export function resourceHueVar(hue: ResourceHue): string {
  return hue === 'neutral' ? 'var(--color-text-muted)' : `var(--res-hue-${hue})`;
}

export interface ResourceIconProps {
  kind: ResourceKind | string;
  size?: number;
  /** false draws it in the muted text color, for dense lists. */
  colored?: boolean;
  /** A color of the resource's own (an object type's color), any CSS color. */
  color?: string;
  title?: string;
  className?: string;
}

/** The icon of a resource type, in its category color. */
export function ResourceIcon({ kind, size = 16, colored = true, color, title, className }: ResourceIconProps) {
  const def = resourceIconDef(kind);
  const hue = color ?? (colored ? resourceHueVar(def.hue) : 'var(--color-text-muted)');
  return React.createElement('span', {
    className: ['ds-res-icon', className].filter(Boolean).join(' '),
    'data-kind': kind,
    'data-hue': colored && !color ? def.hue : undefined,
    style: { ['--res-hue' as string]: hue, width: size, height: size } as React.CSSProperties,
    title,
    'aria-hidden': title ? undefined : true,
    role: title ? 'img' : undefined,
    'aria-label': title,
  }, React.createElement(def.icon, { size, strokeWidth: 1.75 }));
}
